<template>
  <div class="screen-wrap">
    <v-app class="hmc-app">
      <v-main class="main-body">
        <v-container class="app-body">
          <ApiErrorDialog v-if="worldApi.isError" :api="worldApi" />
          <ErrorDialog v-if="error" :error="error" :message="errorMessage" @exitError="exitError" />
          <FinishedBanner v-if="loading" message="loading score..."></FinishedBanner>
          <CompletionGift v-if="showReward && !error" :callback="showScoreScreen" :callbackClose="callbackClose"
            :totalCoins="totalCoins" />
          <div v-if="showScore && !error" class="score-card">
            <div class="text-h5 text-center mb-3">{{ $t("view.PageFunArenaLanding.treasureHuntTitle") }}</div>
            <div class="d-flex justify-center mb-4">
              <v-icon v-for="i in 3" :key="i" :color="i <= star ? 'amber darken-1' : 'grey lighten-1'" x-large>
                mdi-star
              </v-icon>
            </div>
            <div class="d-flex justify-center mt-6">
              <v-btn class="ma-2" color="green" dark @click="playAgain">
                {{ $t("action.playAgain") }}
              </v-btn>
              <v-btn class="ma-2" color="blue darken-3" dark @click="exit">
                {{ $t("action.done") }}
              </v-btn>
            </div>
          </div>
        </v-container>
      </v-main>
    </v-app>
  </div>
</template>

<script>
export default {
  components: {
    FinishedBanner: () =>
      import(
        /* webpackChunkName: "component-mini-game-finished-banner" */ "@/components/games/FinishedBanner"
      ),
    CompletionGift: () =>
      import(
        /* webpackChunkName: "component-fun-arena-loot" */ "@/components/games/FunArenaLoot"
      ),
    ApiErrorDialog: () =>
      import(
        /* webpackChunkName: "component-api-error-dialog" */ "@/components/ApiErrorDialog.vue"
      ),
    ErrorDialog: () =>
      import(
        /* webpackChunkName: "component-error-dialog" */ "@/components/ErrorDialog.vue"
      ),
  },
  data: () => ({
    loading: true,
    showReward: false,
    showScore: false,
    error: false,
    errorMessage: "Failed to process Treasure Hunt results.",
    scoreData: null,
    star: 0,
    countCorrect: 0,
    baseCoins: 0,
    bonusCoins: 0,
    totalCoins: 0,
    worldApi: {
      isLoading: false,
      isError: false,
      error: null,
      url: null,
    },
  }),
  created() {
    const scoreToken = this.$route.query.scoreToken;
    if (!scoreToken) {
      this.error = true;
      this.errorMessage = "No score token provided.";
      this.loading = false;
      return;
    }
    this.fetchScoreData(scoreToken);
  },
  methods: {
    exit() {
      this.$router.push({
        name: "PageWorldFun",
      });
    },
    playAgain() {
      this.$router.push({
        name: "PageFunArenaLanding",
      });
    },
    exitError() {
      this.error = false;
      this.$router.push({
        name: "PageFunArenaLanding",
      });
    },
    showScoreScreen() {
      // Callback when reward gift is shown
    },
    callbackClose() {
      this.showReward = false;
      this.loading = false;
      this.showScore = true;
    },
    fetchScoreData(token) {
      this.worldApi.url = `${process.env.VUE_APP_WORLD_API_URL}/api/game/score/${token}`;
      this.worldApi.callbackReset = () => {
        this.worldApi.isLoading = true;
        this.worldApi.isError = false;
        this.worldApi.error = null;
      };
      this.worldApi.callbackError = (e) => {
        this.worldApi.isLoading = false;
        this.worldApi.isError = true;
        this.worldApi.error = e;
        this.error = true;
        this.errorMessage = "Failed to fetch score data.";
        this.loading = false;
      };
      this.worldApi.callbackSuccess = (resp) => {
        this.worldApi.isLoading = false;
        if (resp.success && resp.data) {
          this.scoreData = resp.data;
          this.processScoreData();
        } else {
          this.error = true;
          this.errorMessage = resp.message || "Failed to load score data.";
          this.loading = false;
        }
      };
      this.worldApi.method = "GET";
      this.$api.fetch(this.worldApi);
    },
    processScoreData() {
      const totalScore = Number(this.scoreData?.score?.total || 0);
      this.star = this.resolveStar(totalScore);
      this.countCorrect = this.resolveCountCorrect(totalScore);
      this.baseCoins = this.countCorrect;
      this.bonusCoins = this.star === 3 ? 5 : 0;
      this.totalCoins = this.baseCoins + this.bonusCoins;

      // Show reward screen
      this.loading = false;
      this.showReward = true;
    },
    resolveStar(totalScore) {
      const rawStar = this.scoreData?.score?.star;
      if (rawStar !== undefined && rawStar !== null && rawStar !== "") {
        const parsedStar = Number(rawStar);
        if (!Number.isNaN(parsedStar)) {
          return Math.max(0, Math.min(3, parsedStar));
        }
      }
      if (totalScore >= 5000) {
        return 3;
      }
      if (totalScore >= 4000) {
        return 2;
      }
      if (totalScore >= 3000) {
        return 1;
      }
      return 0;
    },
    resolveCountCorrect(totalScore) {
      const score = this.scoreData?.score || {};
      const rawCorrect =
        score.countCorrect ??
        score.correct ??
        score.correctCount ??
        score.totalCorrect ??
        score.right;
      if (rawCorrect !== undefined && rawCorrect !== null && rawCorrect !== "") {
        const parsedCorrect = Number(rawCorrect);
        if (!Number.isNaN(parsedCorrect)) {
          return Math.max(0, parsedCorrect);
        }
      }
      if (totalScore <= 0) {
        return 0;
      }
      if (totalScore >= 5000) {
        return 5;
      }
      if (totalScore >= 4000) {
        return 4;
      }
      if (totalScore >= 3000) {
        return 3;
      }
      return 2;
    },
  },
};
</script>

<style scoped>
.app-body {
  max-width: 480px;
  min-height: 100%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-body {
  background-color: rgba(214, 132, 49, 0.3);
}

.score-card {
  background-color: #fff9f1;
  border: #ccaf8b 4px solid;
  border-radius: 10px;
  padding: 32px 24px;
  width: 100%;
  max-width: 400px;
}
</style>
